// src/components/AdminDashboard/sections/ReportingSection.js
import React, { useState, useEffect } from 'react';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Box, Paper } from '@mui/material';
// Removed styled-components import - now using Tailwind classes
import api from '../../../../utils/api';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';

const ReportingSection = () => {
  const [signupData, setSignupData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDailySignups = async () => {
      try {
        setLoading(true);
        const response = await api.get('/reports/daily-signups');
        setSignupData(response.data);
      } catch (err) {
        setError('Failed to fetch daily signup data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDailySignups();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateTotalSignups = () => {
    return signupData.reduce((total, item) => total + item.count, 0);
  };

  const getAverageSignups = () => {
    if (signupData.length === 0) return 0;
    return Math.round(calculateTotalSignups() / signupData.length);
  };

  if (loading) {
    return (
      <Paper className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 mb-8 shadow-admin-card border border-accent-teal/10 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-admin-card-hover hover:border-accent-teal/20 relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-0.5 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100">
        <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-accent-teal/10">
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon className="text-accent-teal mr-2" />
            <Typography variant="h6" className="text-accent-grey font-bold text-xl m-0">
              Daily Signup Reports
            </Typography>
          </Box>
        </div>
        <div className="flex justify-center items-center p-10">
          <CircularProgress className="text-accent-teal" />
        </div>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 mb-8 shadow-admin-card border border-accent-teal/10 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-admin-card-hover hover:border-accent-teal/20 relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-0.5 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100">
        <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-accent-teal/10">
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon className="text-accent-teal mr-2" />
            <Typography variant="h6" className="text-accent-grey font-bold text-xl m-0">
              Daily Signup Reports
            </Typography>
          </Box>
        </div>
        <div className="text-center text-red-500 mt-5 p-4 bg-gradient-to-br from-red-500/10 to-red-600/10 rounded-lg border-l-4 border-red-500 font-semibold">
          {error}
        </div>
      </Paper>
    );
  }

  return (
    <Paper className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 mb-8 shadow-admin-card border border-accent-teal/10 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-admin-card-hover hover:border-accent-teal/20 relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-0.5 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100">
      <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-accent-teal/10">
        <Box display="flex" alignItems="center" gap={1}>
          <TrendingUpIcon className="text-accent-teal mr-2" />
          <Typography variant="h6" className="text-accent-grey font-bold text-xl m-0">
            Daily Signup Reports
          </Typography>
        </Box>
      </div>

      {signupData.length === 0 ? (
        <div className="text-center text-accent-grey mt-8 p-10 bg-gradient-to-br from-accent-teal/5 to-accent-orange/5 rounded-xl border-2 border-dashed border-accent-teal/30 text-lg font-medium">
          No signup data available.
        </div>
      ) : (
        <>
          {/* Enhanced Summary Cards */}
          <Box className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Paper className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 text-center shadow-admin-card border border-accent-teal/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-admin-card-hover relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-0.5 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange">
              <CalendarTodayIcon className="text-4xl text-accent-teal mb-2" />
              <Typography variant="h6" className="text-accent-teal font-bold text-3xl mb-2">
                {signupData.length}
              </Typography>
              <Typography variant="body2" className="text-accent-grey font-medium">
                Days Tracked
              </Typography>
            </Paper>
            <Paper className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 text-center shadow-admin-card border border-accent-teal/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-admin-card-hover relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-0.5 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange">
              <PeopleIcon className="text-4xl text-accent-teal mb-2" />
              <Typography variant="h6" className="text-accent-teal font-bold text-3xl mb-2">
                {calculateTotalSignups()}
              </Typography>
              <Typography variant="body2" className="text-accent-grey font-medium">
                Total Signups
              </Typography>
            </Paper>
            <Paper className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 text-center shadow-admin-card border border-accent-teal/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-admin-card-hover relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-0.5 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange">
              <AssessmentIcon className="text-4xl text-accent-teal mb-2" />
              <Typography variant="h6" className="text-accent-teal font-bold text-3xl mb-2">
                {getAverageSignups()}
              </Typography>
              <Typography variant="body2" className="text-accent-grey font-medium">
                Avg. Daily Signups
              </Typography>
            </Paper>
          </Box>

          {/* Enhanced Data Table */}
          <TableContainer className="rounded-xl shadow-lg">
            <Table className="min-w-[700px] [&_.MuiTableHead-root]:bg-gradient-to-r [&_.MuiTableHead-root]:from-accent-teal [&_.MuiTableHead-root]:to-accent-grey [&_.MuiTableCell-head]:text-white [&_.MuiTableCell-head]:font-bold [&_.MuiTableCell-head]:text-sm [&_.MuiTableCell-head]:uppercase [&_.MuiTableCell-head]:tracking-wider [&_.MuiTableCell-head]:border-none [&_.MuiTableCell-head]:p-4 [&_.MuiTableBody-root_.MuiTableRow-root]:transition-colors [&_.MuiTableBody-root_.MuiTableRow-root]:duration-200 [&_.MuiTableBody-root_.MuiTableRow-root:hover]:bg-accent-teal/5 [&_.MuiTableBody-root_.MuiTableRow-root:nth-of-type(even)]:bg-accent-teal/[0.02] [&_.MuiTableCell-body]:border-b [&_.MuiTableCell-body]:border-accent-grey/10 [&_.MuiTableCell-body]:text-accent-grey [&_.MuiTableCell-body]:font-medium [&_.MuiTableCell-body]:p-4">
              <TableHead>
                <TableRow>
                  <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Date</TableCell>
                  <TableCell align="right" className="text-accent-grey font-bold text-sm uppercase tracking-wider">Signup Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {signupData.map((row, index) => (
                  <TableRow key={index} className="hover:bg-accent-teal/5">
                    <TableCell>{formatDate(row.signupDate)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" className="font-bold">
                        {row.count}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
};

export default ReportingSection; 